import type GUI from 'lil-gui';

/**
 * Schema for a single discoverable UI slider. Used by the visual-confirmation
 * sweep — `scripts/slider-sweep.mjs` enumerates these via `__twin.listSliders`
 * (or `__mapReview.listSliders`), drives each one through its full range,
 * captures the rendered scene, and asserts a non-trivial pixel delta.
 */
export interface SliderInfo {
  /** Folder breadcrumb path, e.g. `Tuning/Physics`. */
  folder: string;
  /** Display label shown next to the control. */
  name: string;
  /** Underlying styleConfig / object property the controller writes to. */
  property: string;
  /** Inferred kind so the sweep knows how to perturb the value. */
  kind: 'number' | 'boolean' | 'color' | 'option' | 'function' | 'unknown';
  min?: number;
  max?: number;
  /** Currently-bound value at probe time. */
  value: unknown;
  /** Static option list when the controller is a dropdown. */
  options?: unknown[];
}

interface ControllerLike {
  property?: string;
  _name?: string;
  _min?: number;
  _max?: number;
  _options?: unknown[] | Record<string, unknown>;
  parent?: { _title?: string; parent?: { _title?: string } };
  getValue?: () => unknown;
  initialValue?: unknown;
}

interface GUILike {
  controllersRecursive?: () => ControllerLike[];
}

function inferKind(controller: ControllerLike, value: unknown): SliderInfo['kind'] {
  if (controller._options) return 'option';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'string') {
    return /^#[0-9a-fA-F]{6}$/.test(value) ? 'color' : 'option';
  }
  if (typeof value === 'function') return 'function';
  return 'unknown';
}

function folderPath(controller: ControllerLike): string {
  const parts: string[] = [];
  let cursor: ControllerLike['parent'] = controller.parent;
  while (cursor) {
    if (cursor._title) parts.unshift(cursor._title);
    cursor = cursor.parent;
  }
  return parts.join('/');
}

function flattenOptions(options: unknown[] | Record<string, unknown> | undefined): unknown[] | undefined {
  if (!options) return undefined;
  if (Array.isArray(options)) return options;
  return Object.values(options);
}

export function listSlidersFromGui(gui: GUILike | null | undefined): SliderInfo[] {
  if (!gui?.controllersRecursive) return [];
  const out: SliderInfo[] = [];
  for (const controller of gui.controllersRecursive()) {
    const value = controller.getValue?.();
    out.push({
      folder: folderPath(controller),
      name: controller._name ?? controller.property ?? '<unnamed>',
      property: controller.property ?? '<no-property>',
      kind: inferKind(controller, value),
      min: controller._min,
      max: controller._max,
      value,
      options: flattenOptions(controller._options),
    });
  }
  return out;
}

export function listSlidersFromGuis(guis: Iterable<GUI | GUILike>): SliderInfo[] {
  const out: SliderInfo[] = [];
  for (const gui of guis) out.push(...listSlidersFromGui(gui as GUILike));
  return out;
}
